// SEAT-DRAWER-MODALS (Andrei, 2026-10-28: "all this performance, seat setup, execution overall
// should have like a little setting that opens the modal, and I can configure everything in that
// modal"): the seat panel's section gear and the modal it opens. The gear sits beside a section's
// title, shows on hover or keyboard focus (always on touch), and is drawn only for a viewer who
// may change that section. The modal configures the section in place: the chart and the panel
// stay where they were, and every save refreshes them under it.
// SEAT-DRAWER-CLICKS: the drawer's item rows open their own modal the same way; a link that leaves
// the chart carries `PageArrow`.
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  useReturnFocus,
} from "@companyos/ui";
import { ArrowUpRightIcon, Settings2Icon } from "lucide-react";
import { type ReactNode, useId } from "react";

/**
 * SEAT-DRAWER-CLICKS (Andrei, 2026-10-28): the drawer's and its modals' few ways to another page,
 * each marked with the external arrow so it is clear the click leaves the chart. Everything else
 * in the drawer opens in place. `children` is a router `<Link>`'s content; the arrow goes after.
 */
export function PageArrow() {
  return (
    <ArrowUpRightIcon
      aria-hidden="true"
      data-slot="page-arrow"
      className="ml-0.5 inline size-3 shrink-0 align-[-1px]"
    />
  );
}

/** The section's gear: "Configure Performance". */
export function SectionGear({ section, onClick }: { section: string; onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={`Configure ${section}`}
      title={`Configure ${section}`}
      data-slot="section-gear"
      onClick={onClick}
      className={cn(
        "text-faint hover:text-foreground",
        // Shown on the section's hover and on keyboard focus; always on touch screens.
        "opacity-0 group-hover/section:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
      )}
    >
      <Settings2Icon aria-hidden="true" />
    </Button>
  );
}

/**
 * A section's modal: a title with its (i), one short line, the section's editors, and Done. The
 * quick adds it opens (a KPI, a responsibility, a priority) open over it as nested dialogs.
 */
export function SectionModal({
  title,
  info,
  description,
  children,
  footer,
  onClose,
  slot,
  size = "lg",
}: {
  title: string;
  info?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Replaces the Done button (a form's Cancel and Save). */
  footer?: ReactNode;
  onClose: () => void;
  slot: string;
  /** `xl` for a full table (the seat's KPIs). */
  size?: "default" | "lg" | "xl";
}) {
  const opener = useReturnFocus();
  const titleId = useId();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          size === "lg" && "sm:max-w-[760px]",
          size === "xl" && "sm:max-w-[min(1040px,calc(100%-2rem))]",
        )}
        finalFocus={opener}
        aria-labelledby={titleId}
        data-section-modal={slot}
      >
        <DialogHeader>
          <span className="flex items-center gap-1.5 pr-8">
            <DialogTitle id={titleId} className="pr-0">
              {title}
            </DialogTitle>
            {info ? <InfoTip label={`About ${title.toLowerCase()}`}>{info}</InfoTip> : null}
          </span>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-4" data-slot={slot}>
          {children}
        </div>
        <DialogFooter>
          {footer ?? (
            <Button type="button" variant="secondary" onClick={onClose}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
